import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import type { AlertColor } from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useId, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { FeedbackSnackbar } from '../../../components/FeedbackSnackbar';
import { apiErrorMessageKey } from '../../../lib/api';
import { selectActiveMembership, useCurrentUser } from '../../../lib/auth';
import { useInviteMember, useRemoveMember, useTeamMembers, type TeamMember } from '../api/teamApi';
import { InviteMemberDialog } from '../components/InviteMemberDialog';
import { MembersList } from '../components/MembersList';
import { RemoveMemberDialog } from '../components/RemoveMemberDialog';
import { SettingsQueryGate } from '../components/SettingsQueryGate';

export function MembersSettingsPage(): ReactElement {
    const { t } = useTranslation();
    const currentUser = useCurrentUser();
    const query = useTeamMembers();
    const companyName = selectActiveMembership(currentUser)?.partnerName ?? '';
    const headingId = useId();

    const [inviteOpen, setInviteOpen] = useState(false);
    const [memberToRemove, setMemberToRemove] = useState<TeamMember | undefined>(undefined);
    const [toast, setToast] = useState<{ severity: AlertColor; title?: string; message: string } | undefined>(
        undefined,
    );

    const inviteMutation = useInviteMember();
    const removeMutation = useRemoveMember();

    const handleInvite = (email: string): void => {
        inviteMutation.mutate(email, {
            onSuccess: () => {
                setInviteOpen(false);
                inviteMutation.reset();
                setToast({
                    severity: 'success',
                    title: t('SETTINGS.MEMBERS.INVITE_SENT_TITLE'),
                    message: t('SETTINGS.MEMBERS.INVITE_SENT_BODY', { email, companyName }),
                });
            },
        });
    };

    const handleConfirmRemove = (): void => {
        if (!memberToRemove) {
            return;
        }
        removeMutation.mutate(memberToRemove.uuid, {
            onSuccess: () => {
                setMemberToRemove(undefined);
                removeMutation.reset();
            },
        });
    };

    const closeInvite = (): void => {
        setInviteOpen(false);
        inviteMutation.reset();
    };

    const closeRemove = (): void => {
        setMemberToRemove(undefined);
        removeMutation.reset();
    };

    return (
        <>
            <Card variant="outlined" component="section" aria-labelledby={headingId}>
                <CardHeader
                    title={t('SETTINGS.MEMBERS.TITLE')}
                    slotProps={{ title: { variant: 'h6', component: 'h2', id: headingId } }}
                />
                <CardContent>
                    <Stack spacing={1}>
                        <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            alignItems={{ xs: 'stretch', md: 'center' }}
                            justifyContent="space-between"
                            spacing={{ xs: 1, md: 3 }}>
                            <Typography variant="body2" color="text.secondary" sx={{ order: { xs: 2, md: 0 } }}>
                                {t('SETTINGS.MEMBERS.SUBTITLE')}
                            </Typography>
                            <Button
                                variant="contained"
                                startIcon={<PersonAddAltOutlinedIcon />}
                                onClick={() => setInviteOpen(true)}
                                sx={{ order: { xs: 1, md: 0 } }}>
                                {t('SETTINGS.MEMBERS.INVITE_BUTTON')}
                            </Button>
                        </Stack>
                        <SettingsQueryGate
                            query={query}
                            loading={
                                <Stack spacing={1} aria-busy>
                                    <Skeleton variant="rounded" height={56} />
                                    <Skeleton variant="rounded" height={56} />
                                    <Skeleton variant="rounded" height={56} />
                                </Stack>
                            }>
                            {(members) => (
                                <MembersList
                                    members={members}
                                    currentUserEmail={currentUser?.email}
                                    onRemove={setMemberToRemove}
                                />
                            )}
                        </SettingsQueryGate>
                    </Stack>
                </CardContent>
            </Card>

            <InviteMemberDialog
                open={inviteOpen}
                companyName={companyName}
                isSubmitting={inviteMutation.isPending}
                errorMessage={
                    inviteMutation.error
                        ? t(apiErrorMessageKey(inviteMutation.error, 'SETTINGS.MEMBERS.ERRORS.INVITE_FAILED'))
                        : undefined
                }
                onSubmit={handleInvite}
                onCancel={closeInvite}
            />
            <RemoveMemberDialog
                member={memberToRemove}
                isSubmitting={removeMutation.isPending}
                errorMessage={
                    removeMutation.error
                        ? t(apiErrorMessageKey(removeMutation.error, 'SETTINGS.MEMBERS.ERRORS.REMOVE_FAILED'))
                        : undefined
                }
                onConfirm={handleConfirmRemove}
                onCancel={closeRemove}
            />
            <FeedbackSnackbar
                open={toast !== undefined}
                severity={toast?.severity ?? 'success'}
                variant="standard"
                title={toast?.title}
                message={toast?.message ?? ''}
                onClose={() => setToast(undefined)}
            />
        </>
    );
}
